import { isBrunoUserId } from '@shared/bruno/constants'
import { RECORDING_POLICY_CAR_UNDECIDED_MESSAGE } from '@shared/calls/recording-consent'
import { listDirectoryUsers, getCurrentChatUser } from '../../../services/chat-service'
import { getMyAccountPermissions } from '../../../services/member-access-service'
import { resolveAvatarDisplayUrl } from '../../../services/profile-service'
import {
  clearNativeCarRecents,
  replaceNativeCarContacts,
} from '../../../services/voice-call/native-call'
import type { ChatUser } from '../../../types/chat'
import { isCallAlreadyEndedError } from '../../../utils/format-error'
import { isNativeApp } from '../../../utils/platform'
import { isIndependentUser, isMemberUserRole } from '../../../utils/rbac'

/**
 * `address`/`lat`/`lng` feed the car "Navigate to contact" action (hidden
 * when absent). The directory/profile data carries NO address fields today —
 * the pipe is wired end-to-end so Navigate lights up the moment the backend
 * starts supplying them (sessions got coordinates the same way).
 */
export type CarContact = {
  peerId: string
  peerName: string
  avatarUrl?: string | null
  address?: string
  lat?: number
  lng?: number
}

export type SpokenCarContactQuery = {
  identifier?: string | null
  spoken?: string | null
}

/**
 * Permitted car-dial roster: the same directory the phone Calls page uses
 * (`listUsers` → `listDirectoryUsers`), minus Bruno, self, and anyone without
 * a display name. Independents / Members with account `calls` disabled get
 * an empty list (fail closed). The server `listUsers` filter is the no-leak
 * guarantee — this never adds people the directory would hide.
 *
 * `avatarUrl` is the stored directory URL. {@link syncCarContactsFromDirectory}
 * resolves it to a display URL (presigned when private S3) before the native
 * snapshot, so Android Auto / CarPlay can fetch the bitmap without the WebView.
 */
export function buildCarPermittedContacts(
  users: Array<Pick<ChatUser, 'userId' | 'displayName' | 'avatarUrl'>>,
  options: { selfUserId?: string | null; callsEnabled?: boolean },
): CarContact[] {
  if (options.callsEnabled === false) return []
  return users
    .filter((u) => u.userId && u.userId !== options.selfUserId && !isBrunoUserId(u.userId))
    .map((u) => {
      const peerName = (u.displayName ?? '').trim()
      const avatarUrl = (u.avatarUrl ?? '').trim()
      const row: CarContact = { peerId: u.userId, peerName }
      if (avatarUrl) row.avatarUrl = avatarUrl
      return row
    })
    .filter((c) => c.peerName.length > 0)
    .sort((a, b) => a.peerName.localeCompare(b.peerName, undefined, { sensitivity: 'base' }))
}

/**
 * Resolve a Siri / `INStartCallIntent` spoken name against the PERMITTED
 * contacts set only. Never falls back to "first recent" or a name outside
 * this list (that would leak a cold contact or dial the wrong person).
 *
 * Precedence: exact `peerId` identifier, then unique case-insensitive exact
 * name, then unique substring. Ambiguous matches return null.
 */
export function matchSpokenCarContact(
  contacts: CarContact[],
  query: SpokenCarContactQuery,
): CarContact | null {
  const identifier = query.identifier?.trim()
  if (identifier) {
    const byId = contacts.find((c) => c.peerId === identifier)
    if (byId) return byId
  }
  const spoken = query.spoken?.trim()
  if (!spoken) return null
  const lower = spoken.toLowerCase()
  const exact = contacts.filter((c) => c.peerName.toLowerCase() === lower)
  if (exact.length === 1) return exact[0]
  if (exact.length > 1) return null
  const contains = contacts.filter((c) => c.peerName.toLowerCase().includes(lower))
  if (contains.length === 1) return contains[0]
  return null
}

export function carContactLetter(name: string): string {
  const ch = name.trim().charAt(0).toUpperCase()
  return ch >= 'A' && ch <= 'Z' ? ch : '#'
}

export type CarContactListGroup =
  | { kind: 'names'; items: CarContact[] }
  | { kind: 'letters'; items: string[] }
  | { kind: 'ranges'; items: Array<{ label: string; letters: string[] }> }

/**
 * Fit an alphabetical roster into a car grid/list cap (Android Auto
 * ConstraintManager / CarPlay). The ROOT is a letter-tile matrix (or
 * letter-range buckets when the alphabet overflows). Names are only
 * returned after the caller has already narrowed to a single letter
 * (`letterFirst: false`).
 */
export function groupCarContactsForLimit(
  contacts: CarContact[],
  limit: number,
  options: { letterFirst?: boolean } = { letterFirst: true },
): CarContactListGroup {
  const cap = Math.max(1, limit)
  const letterFirst = options.letterFirst !== false
  if (!letterFirst) {
    if (contacts.length <= cap) return { kind: 'names', items: contacts }
  }
  if (contacts.length === 0) return { kind: 'names', items: contacts }
  const letters: string[] = []
  for (const c of contacts) {
    const letter = carContactLetter(c.peerName)
    if (!letters.includes(letter)) letters.push(letter)
  }
  if (!letterFirst && letters.length <= 1) {
    return { kind: 'names', items: contacts.slice(0, cap) }
  }
  if (letters.length <= cap) return { kind: 'letters', items: letters }
  const size = Math.ceil(letters.length / cap)
  const ranges: Array<{ label: string; letters: string[] }> = []
  for (let i = 0; i < letters.length; i += size) {
    const chunk = letters.slice(i, i + size)
    const label = chunk.length === 1 ? chunk[0] : `${chunk[0]}–${chunk[chunk.length - 1]}`
    ranges.push({ label, letters: chunk })
  }
  return { kind: 'ranges', items: ranges }
}

/** True when a car-originated INVITE failed the #342/#350 recording-policy gate. */
export function isCarRecordingPolicyRejected(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '')
  return message.includes(RECORDING_POLICY_CAR_UNDECIDED_MESSAGE)
}

export function resolveCarStartFailureAlert(error: unknown): string | null {
  if (isCarRecordingPolicyRejected(error)) return RECORDING_POLICY_CAR_UNDECIDED_MESSAGE
  // The call was ended (e.g. End on the car) before its INVITE landed.
  if (isCallAlreadyEndedError(error)) return null
  const message = error instanceof Error ? error.message : String(error ?? '')
  if (/already in a call/i.test(message)) return "You're already on a call."
  if (/not signed in/i.test(message)) return 'Sign in on your phone, then try again.'
  if (message.trim()) return "Couldn't start the call. Try again."
  return null
}

/** Signed avatar URLs requested at once: the directory can hold thousands of people. */
const AVATAR_URL_CONCURRENCY = 8

/** `fn` over `items` with at most `limit` in flight; results keep the input order. */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length)
  let next = 0
  const worker = async () => {
    while (next < items.length) {
      const index = next
      next += 1
      results[index] = await fn(items[index])
    }
  }
  await Promise.all(Array.from({ length: Math.min(Math.max(limit, 1), items.length) }, worker))
  return results
}

/**
 * Push the permitted directory snapshot into the native CarPlay / Android Auto
 * contacts store. Fire-and-forget: the car UI shows empty until the phone app
 * has run once and synced.
 */
export async function syncCarContactsFromDirectory(): Promise<void> {
  if (!isNativeApp()) return
  try {
    const [users, me] = await Promise.all([listDirectoryUsers(), getCurrentChatUser()])
    let callsEnabled = true
    if (isIndependentUser(me) || isMemberUserRole(me)) {
      try {
        const permissions = await getMyAccountPermissions()
        callsEnabled = permissions.calls !== 'disabled'
      } catch {
        // Fail closed: do not leak a roster we cannot prove is callable.
        callsEnabled = false
      }
    }
    const contacts = buildCarPermittedContacts(users, {
      selfUserId: me.userId,
      callsEnabled,
    })
    const withDisplayAvatars = await mapWithConcurrency(contacts, AVATAR_URL_CONCURRENCY, async (c) => {
      if (!c.avatarUrl) return c
      const display = await resolveAvatarDisplayUrl(c.avatarUrl)
      return display ? { ...c, avatarUrl: display } : { peerId: c.peerId, peerName: c.peerName }
    })
    await replaceNativeCarContacts(withDisplayAvatars)
  } catch {
    /* best-effort — car UI stays on the last good snapshot */
  }
}

/** Sign-out: wipe the native snapshot so the next account cannot see it. */
export async function clearCarContactsSnapshot(): Promise<void> {
  if (!isNativeApp()) return
  try {
    await replaceNativeCarContacts([])
    await clearNativeCarRecents()
  } catch {
    /* best-effort */
  }
}
